// @flow

import React, { useCallback, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { Color, flex, Layout, maxWidthContainer, topBottomMargin, Width, buttonDefault } from 'styles';
import { MarketsHero } from 'components/Content';
import { LogoMarkets } from 'components/Svg';
import { Button, ImageLoader } from 'components/Gui';
import axios from 'axios';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex()};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
`;

const Blurb = styled.div`
  width: 100%;
  background: white;
  padding-bottom: 40px;
  border-bottom: 1px solid ${Color.MARKETS_RED};

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex('flex-start', 'flex-start')};
    flex-direction: column;

    div.form {
      width: 100%;
      ${flex('flex-start', 'flex-start')};
      margin-top: 40px;

      div.image {
        width: 260px;
        flex: 0 0 auto;
        display: none;

        @media (min-width: ${Width.BREAK_MD}px) {
          display: block;
        }
      }

      div.inputs {
        width: 100%;
        flex: 0 0 auto;

        @media (min-width: ${Width.BREAK_MD}px) {
          width: 460px;
        }

        > div {
          &:first-of-type {
            ${flex('flex-start', 'flex-start')};
            flex-wrap: wrap;

            input {
              width: 200px;
              margin-right: 20px;
              ${buttonDefault};
              font-weight: 400;
              font-size: 2rem;
              margin-bottom: 20px;
              border-bottom: 1px solid ${Color.BLACK_SOUL};
            }

            textarea {
              width: 420px;
              ${buttonDefault};
              font-weight: 400;
              font-size: 2rem;
              margin-bottom: 20px;
              margin-top: 20px;
              height: 100px;
              border-bottom: 1px solid ${Color.BLACK_SOUL};
            }
          }
        }
      }
    }

    p,
    ul,
    li {
      font-size: 1.7rem;
      text-align: left;
    }

    ul {
      margin-left: 30px;
    }

    h2 {
      width: 100%;
      text-align: center;
      margin: 40px 0;
    }

    h3 {
      font-size: 1.7rem;
      margin: 20px 0;
    }

    > div.logo {
      align-self: flex-start;
      position: relative;
      z-index: 2;
      background: black;
      padding: 10px;
      ${flex()};
      margin-bottom: 40px;

      svg {
        width: 100px;

        path,
        rect,
        polygon {
          fill: white;
        }
      }
    }

    div.blurb {
      margin-top: 20px;
      ${flex()};
      flex-direction: column;

      @media (min-width: ${Width.BREAK_MD}px) {
        flex-direction: row;
        ${flex('space-between', 'flex-start')};
      }

      > div {
        flex: 0 0 auto;
        width: 100%;
        margin-bottom: 40px;

        @media (min-width: ${Width.BREAK_MD}px) {
          width: 260px;
          margin-right: 40px;
          margin-bottom: 0;
        }
      }
    }
  }
`;

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);
  const [formData, setFormData] = useState({});
  const onSubmit = useCallback(() => {
    const go = async () => {
      if (formData.firstName && formData.lastName && formData.email && formData.inquiry) {
        await axios.post('http://54.174.87.76/api/message/markets_direct', {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          language: 'en',
          inquiry: formData.inquiry,
        });
      }
    };
    go();
  }, [formData]);
  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('b2b'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <MarketsHero
          title={`B2B`}
          blurb="Break away from the crowded one dimensional Forex broker world"
          image={{
            name: 'b2b',
          }}
          images={data}
        />
        <Blurb>
          <div>
            <div className="logo">{LogoMarkets}</div>
            <p>{`Markets Direct welcomes Forex brokers and other institutions with an exciting opportunity to significantly increase your client reach, global footprint, and bottom line.
            Break away from the crowded one dimensional Forex broker world. Multi-asset trading is the new and proven direction for traders and investors today.
            Our turnkey white label solution will launch your business above the competition and attract a whole new cadre of traders and investors.
            Your clients can now trade and invest in US listed stocks, Futures, and Forex from a single cross-margined account.`}</p>
            <ul>
              <li>Access market data from the world's exchanges while trading both on and off exchange.</li>
              <li>Commission-free trading* on US stocks is a proven and powerful draw to attract a whole new group of clients.</li>
              <li>As a unique added benefit, ask how you can utilize our banking solution.</li>
            </ul>
            <h2>"Its a big world. Open it up for your clients." </h2>
            <h3>Connect with us.</h3>
            <p>{`Connection with Markets Direct is quick and simple. Choose your front ends from among MT5, CQG Desktop, and mobile.`}</p>
            <h3>Connect to your customers</h3>
            <ul>
              <li>We offer complete hosting services, or host from your side.</li>
              <li>
                Customer orders are routed to and filled on global Exchanges, ensuring "Best Execution" and full compliance with
                regulatory standards.
              </li>
              <li>Low cost - benefit from our cost effective pricing arrangements.</li>
              <li>Subscriber services available for dynamic futures margining, stock dividends, and corporate actions.</li>
            </ul>
            <h3>For brokers with an existing MT5 server(s)</h3>
            <p>
              {`Connection is easy, MT5 to MT5. Easy access through our MT5 Gateway.
              Our CQG/MT5 Gateway solution has been in production for three years, engineered specifically to support Equities, Futures, FX and other instruments on a scalable basis.`}
            </p>
            <ul>
              <li>Affordable and competitive implementation and maintenance cost</li>
              <li>Full compliance documentation</li>
              <li>API Connection</li>
            </ul>
            <h3>For Brokers with MT4 or other trading platforms</h3>
            <ul>
              <li>Connect via FIX protocol</li>
              <li>Secure connection</li>
              <li>Tailored solutions to fit your preferences and requirements</li>
            </ul>
            <h3>White Label Solutions</h3>
            <ul>
              <li>Easy and fast integration time</li>
              <li>Markets Direct multi-asset dealing platform built to specs with your company's branding</li>
              <li>24/7 multi-lingual support</li>
            </ul>
            <div className="form">
              <div className="inputs">
                <div>
                  <input
                    onChange={({ target: { value } }) =>
                      setFormData((data) => ({
                        ...data,
                        firstName: value,
                      }))
                    }
                    placeholder="First Name"
                    type="text"
                    name="fname"
                    autoComplete={`given-name`}
                  />
                  <input
                    onChange={({ target: { value } }) =>
                      setFormData((data) => ({
                        ...data,
                        lastName: value,
                      }))
                    }
                    placeholder="Last Name"
                    type="text"
                    name="lname"
                    autoComplete={`family-name`}
                  />
                  <input
                    onChange={({ target: { value } }) =>
                      setFormData((data) => ({
                        ...data,
                        email: value,
                      }))
                    }
                    placeholder="Email"
                    type="text"
                    name={`email`}
                    autoComplete={`email`}
                  />
                  <textarea
                    onChange={({ target: { value } }) =>
                      setFormData((data) => ({
                        ...data,
                        inquiry: value,
                      }))
                    }
                    placeholder="Inquiry Details"
                  />
                </div>
                <Button className="button" onClick={onSubmit}>
                  Submit
                </Button>
              </div>
              <div className="image">
                <ImageLoader image={getImage('contact-hero')} />
              </div>
            </div>
          </div>
        </Blurb>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    content: contentfulAbout {
      hero {
        title
        node_locale
      }
    }
    imageToWebP: allFile(
      filter: { relativeDirectory: { in: ["pages/about", "pages/contact-us", "pages/b2b"] }, extension: { nin: ["svg", "gif"] } }
    ) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(
      filter: { relativeDirectory: { in: ["pages/about", "pages/contact-us"] }, extension: { in: ["svg", "gif"] } }
    ) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
